import React from "react";

import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  makeStyles,
} from "@material-ui/core";

import { DistrictsTable } from "@hubcapp/components";
import Button from "@ds-developer/button";

import { DistrictCoverageProps, ViewMode } from "./types";
import DistrictCoverageSkeleton from "./components/Skeleton";
import ViewDistricts from "./components/ViewDistricts";

type PaddingProps = {
  isEditing: boolean;
};

const useStyles = makeStyles(theme => ({
  action: {
    marginTop: 0,
  },
  cardContent: {
    padding: ({ isEditing }: PaddingProps) =>
      isEditing ? 0 : theme.spacing(2),
    paddingTop: ({ isEditing }: PaddingProps) =>
      isEditing ? 30 : theme.spacing(2),
    "&:last-child": {
      paddingBottom: ({ isEditing }: PaddingProps) =>
        isEditing ? 0 : theme.spacing(2),
    },
  },
}));

const DistrictCoverage: React.FC<DistrictCoverageProps> = ({
  loading = false,
  canEdit,
  districts = [],
  mode = ViewMode.View,
  areaName,
  areaShortCode,
  onDistrictsUpdate = () => {},
  onConfirm = () => {},
  toggleEdit = () => {},
  totalDistricts = 0,
  title,
}) => {
  const isEditing = mode === ViewMode.Edit;
  const classes = useStyles({
    isEditing,
  });

  let buttonText = "View districts";
  let buttonAction = toggleEdit;

  if (canEdit) {
    if (isEditing) {
      // Can edit and is currently editing
      buttonText = "Confirm changes";
      buttonAction = onConfirm;
      title = "Edit my district coverage";
    } else {
      // Can edit but isn't currently
      buttonText = "Edit districts";
    }
  } else if (isEditing) {
    // Can't edit but is viewing edit view in read only mode
    buttonText = "Close";
    title = "My district coverage";
  }

  const Content = () =>
    isEditing ? (
      <DistrictsTable
        tableData={districts}
        readOnly={!canEdit}
        onUpdate={onDistrictsUpdate}
      />
    ) : (
      <ViewDistricts
        districts={districts.filter(district =>
          Boolean(district?.tableData?.checked),
        )}
        areaName={areaName}
        areaShortCode={areaShortCode}
        totalDistricts={totalDistricts}
      />
    );

  return (
    <Card data-test="district-coverage-card">
      <CardHeader
        title={title}
        classes={{ action: classes.action }}
        action={
          <React.Fragment>
            {!loading && (
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={buttonAction}
                data-test="district-coverage-edit-view-button"
              >
                {buttonText}
              </Button>
            )}
          </React.Fragment>
        }
      />
      <Divider />
      <CardContent className={classes.cardContent}>
        {loading ? <DistrictCoverageSkeleton /> : <Content />}
      </CardContent>
    </Card>
  );
};

export default DistrictCoverage;
